import { AlertTriangleIcon, GitBranchAIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Dialog, FeaturedIcon, Stack, Text } from '@prophecy/ui-v3';
import { formatBytes } from '@prophecy/utils/fmt';
import { pluralizeWithNumber, pluralVerb } from '@prophecy/utils/string';
import styled from 'styled-components';

import { LocalCheckoutDeletion } from './types';

const FailureList = styled.div`
  background: ${theme.colors.gray50};
  border-radius: ${theme.radius.x8};
  padding: ${theme.spaces.x12};
  max-height: ${theme.width.x320};
  overflow-y: auto;
`;

type PartialFailureDetailModalProps = {
  open: boolean;
  deletedCount: number;
  requestedCount: number;
  storageFreedBytes: number;
  failures: LocalCheckoutDeletion[];
  retrying: boolean;
  onClose: () => void;
  onRetry: () => void;
};

export function PartialFailureDetailModal({
  open,
  deletedCount,
  requestedCount,
  storageFreedBytes,
  failures,
  retrying,
  onClose,
  onRetry
}: PartialFailureDetailModalProps) {
  const failedCount = failures.length;

  return (
    <Dialog
      open={open}
      size='l'
      closable={!retrying}
      onClose={onClose}
      onOk={onRetry}
      icon={
        <FeaturedIcon variant='light' size='l' tone={BaseColor.warning} icon={<AlertTriangleIcon type='default' />} />
      }
      closeButton={{ children: 'Close', disabled: retrying }}
      okButton={{
        children: `Try again (${failedCount})`,
        loading: retrying,
        disabled: retrying || failedCount === 0
      }}
      title={`${pluralizeWithNumber(failedCount, 'branch', 'es')} couldn't be deleted`}
      data-test-id='partial-failure-detail-modal'>
      <Stack gap={theme.spaces.x16} padding={`${theme.spaces.x16} 0 0 0`}>
        <Text level='sm' tone={theme.colors.gray600}>
          {deletedCount} of {requestedCount} branches were deleted, freeing up {formatBytes(storageFreedBytes)}. The{' '}
          {failedCount} below {pluralVerb(failedCount, 'is', 'are')} still listed on this page.
        </Text>
        <FailureList data-test-id='partial-failure-detail-list'>
          <Stack gap={theme.spaces.x12}>
            {failures.map((failure) => (
              <Stack
                key={failure.userRepositoryUid}
                gap={theme.spaces.x4}
                data-test-id={`partial-failure-row-${failure.userRepositoryUid}`}>
                <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
                  <GitBranchAIcon type='default' size='xs' color={theme.colors.gray500} />
                  <Text level='sm' weight={theme.fontWeight.medium}>
                    {failure.branch || failure.userRepositoryUid}
                  </Text>
                </Stack>
                <Text level='sm' tone={theme.colors.gray600}>
                  {failure.reason ?? 'Unknown error'}
                </Text>
              </Stack>
            ))}
          </Stack>
        </FailureList>
      </Stack>
    </Dialog>
  );
}
